import { Fragment, useState, useEffect } from "react";
import CustomSelect, { Option } from "../../../../custom_components/CustomSelect";
import useUrlParams from "../../../../hooks/useUrlParams";
import ModalInput from "../../../../custom_components/ModalInput";
import './Console.scss'
import useTrueTwin from "../../../../hooks/useTrueTwin";

interface PayloadFormProps {
  allPayloadComponents: any[];
}
const PayloadForm: React.FC<PayloadFormProps> = ({ allPayloadComponents }) => {

  const [selectedPayload, setSelectedPayload] = useState<Option | null>(null);
  const [sequences, setSequences] = useState<any>([]);
  const [selectedSequences, setSelectedSequences] = useState<Option | null>(null);
  const [seqParams, setSeqParams] = useState<any>("");
  const [seqPowerReq, setSeqPowerReq] = useState<any>("");
  const [seqDuration, setSeqDuration] = useState<any>(1);
  const [seqMinDuration, setSeqMinDuration] = useState<any>(1);
  const [seqMaxDuration, setSeqMaxDuration] = useState<any>(1);
  const [OtherInput, setOtherInput] = useState<any>(false);
  const [otherSequence, setOtherSequence] = useState<any>("");

  const { scenario_id } = useUrlParams()
  const { truetwin_id } = useTrueTwin()

  useEffect(() => {
    sessionStorage.setItem('currentForm', 'payload');
    window.dispatchEvent(new Event('storageUpdate'));
  }, []);

  useEffect(() => {
    sessionStorage.setItem("selectedPayload", JSON.stringify(selectedPayload));
    sessionStorage.setItem("selectedSequences", JSON.stringify(selectedSequences));
    sessionStorage.setItem("seqParams", seqParams);
    sessionStorage.setItem("seqPowerReq", seqPowerReq);
    sessionStorage.setItem("seqDuration", seqDuration);
    window.dispatchEvent(new Event("storageUpdate"));
    if (selectedPayload !== null && selectedSequences !== null) {
      resetPayloadForm();
    }
  }, [selectedPayload, selectedSequences, seqParams, seqPowerReq, seqDuration]);

  useEffect(() => {
    const clearFormFields = () => {
      setSelectedPayload(null);
      setSelectedSequences(null);
      setSeqParams('');
      setSeqPowerReq('');
      setSeqDuration(1);
      setOtherSequence('');
      setOtherInput(false);
      sessionStorage.setItem('value', '');
      sessionStorage.setItem('currentForm', 'payload');
      resetPayloadForm()
    };

    window.addEventListener('clearFormFields', clearFormFields);
    return () => {
      window.removeEventListener('clearFormFields', clearFormFields);
    };
  }, []);

  const setPayloadFormData = (data) => {
    const formData = [{
      "TruetwinId": truetwin_id,
      "LinkId": "",
      "scenarioId": scenario_id,
      "Timestamp": null,
      "Type": "completeJson",
      "ScheduleId": "default",
      "TaskId": "default",
      "sequenceData": data?.sequenceData
    }];
    sessionStorage.setItem('formData', JSON.stringify(formData));
    return formData;
  }

  const resetPayloadForm = () => {
    const sequence_duration = Number(sessionStorage.getItem('seqDuration'))
    const selectedSequences = JSON.parse(sessionStorage.getItem('selectedSequences') || '{}');
    const value_1 = selectedSequences?.label === "Other Sequence" ? sessionStorage.getItem('value') : selectedSequences?.value?.seqName;

    const newData = {
      commandName: sessionStorage.getItem("selectedPayload") ? JSON.parse(sessionStorage.getItem("selectedPayload")!).value?.payload?.name || 'Unknown Payload' : 'Unknown Payload',
      commandType: "Payload",
      sequenceData: {
        commandId: 600,
        commandData: {
          App_id: sessionStorage.getItem("selectedPayload") ? JSON.parse(sessionStorage.getItem("selectedPayload")!).value?.payload?.appId || null : null,
          Sequence_id: value_1,
          Sequence_params: sessionStorage.getItem("seqParams"),
          /** Keeping mins and miliseconds 0 as in the input we are accepting duration in seconds only */
          Scheduled_deadline_min: 0,
          Scheduled_deadline_sec: sequence_duration,
          Scheduled_deadline_ms: 0,
        },
        commandHeader: {
          SaId: 1,
          DaId: 134,
          Qos: 3,
        },
      },
    };
    setPayloadFormData(newData);
  };

  const handleManualInputChange = (value) => {
    sessionStorage.setItem("value", value);
    const newData = {
      commandName: selectedPayload?.value?.payload?.name || "Unknown Payload",
      commandType: "Payload",
      sequenceData: {
        commandId: 600,
        commandData: {
          App_id: selectedPayload?.value?.payload?.appId,
          Sequence_id: value,
          Sequence_params: sessionStorage.getItem("seqParams"),
          Scheduled_deadline_min: Math.floor(seqDuration / 60),
          Scheduled_deadline_sec: seqDuration % 60,
          Scheduled_deadline_ms: (seqDuration % 60) * 1000,
        },
        commandHeader: {
          SaId: 1,
          DaId: 134,
          Qos: 3,
        },
      },
    };
    setPayloadFormData(newData);
    setSelectedSequences(value !== '' ? {
      "label": "Other Sequence",
      "value": {},
      "style": "unique_option"
    } : null)
    window.dispatchEvent(new Event("storage"));
  };

  const clearPayloadForm = () => {
    setOtherSequence("");
    setOtherInput(false);
    setSelectedSequences(null);
    setSeqParams('');
    setSeqPowerReq('');
    setSeqDuration(1);
  }

  const handleOtherInputSequenceChange = (e) => {
    const value = e.target.value;
    setOtherSequence(value);
    handleManualInputChange(value);
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event('storageUpdate'));
  }

  const handleSequenceChange = (data) => {
    if (data?.label === "Other Sequence") {
      setOtherInput(true);
      setOtherSequence("");
      setSelectedSequences(null);
      setSeqParams('');
      setSeqPowerReq('');
      setSeqDuration(1);
    } else {
      setSelectedSequences(data)
      sessionStorage.setItem('selectedSequences', JSON.stringify(data))
      if (data?.value?.seqParams || data?.value?.seqParams === '') {
        setSeqParams(data?.value?.seqParams)
        sessionStorage.setItem('seqParams', data?.value?.seqParams);
      }
      if (data?.value?.seqPowerRequirements) {
        setSeqPowerReq(data?.value?.seqPowerRequirements);
        sessionStorage.setItem("seqPowerReq", data?.value?.seqPowerRequirements);
      }
      if (data?.value?.seqDuration) {
        setSeqDuration(data?.value?.seqDuration);
        sessionStorage.setItem("seqDuration", data?.value?.seqDuration);
      }
      let totalSeconds = data?.value?.seqDuration ? data?.value?.seqDuration : seqDuration;
      const totalMinutes = Math.floor(totalSeconds / 60);
      const remainingSeconds = totalSeconds % 60;
      const remainingMilliseconds = (totalSeconds - totalMinutes * 60 - remainingSeconds) * 1000;
      let newData = {
        commandName: selectedPayload?.value?.payload?.name,
        commandType: "Payload",
        sequenceData: {
          commandId: 600,
          commandData: {
            App_id: selectedPayload?.value?.payload?.appId,
            Sequence_id: data?.value?.seqName,
            Sequence_params: data?.value?.seqParams,
            Scheduled_deadline_min: totalMinutes,
            Scheduled_deadline_sec: remainingSeconds,
            Scheduled_deadline_ms: remainingMilliseconds,
          },
          commandHeader: {
            SaId: 1,
            DaId: 134,
            Qos: 3,
          },
        },
      };
      setPayloadFormData(newData);
      window.dispatchEvent(new Event("storage"));
    }
  }

  const handlePayloadSelection = (data) => {
    setSelectedPayload(data);
    const sequencesData = data?.value?.sequence;
    let sequences: Option[] = [];
    if (Array.isArray(sequencesData)) {
      sequences = sequencesData.map((sequence) => ({
        label: sequence?.seqName,
        value: sequence,
      }));
    }
    setSequences([...sequences, { label: "Other Sequence", value: {}, style: "unique_option" }]);
    sessionStorage.setItem("selectedPayload", JSON.stringify(data));
    window.dispatchEvent(new Event("storage"));
  }

  return (
    <Fragment>
      <div className="left__panel__body__input__dropdown__container">
        <span className="input__label">Payload</span>
        <CustomSelect
          options={allPayloadComponents}
          placeholder="Select a payload"
          value={selectedPayload}
          onChange={(data: Option) => { handlePayloadSelection(data) }}
          style={{ height: '28px' }}
          fontSize={'12px'}
          top={'28px'}
        />
      </div>
      {!OtherInput && (
        <div className="left__panel__body__input__dropdown__container left__panel__body_input__border">
          <span className="input__label">Sequence</span>
          <CustomSelect
            options={sequences}
            value={selectedSequences}
            placeholder="Select a sequence"
            onChange={(data) => { handleSequenceChange(data) }}
            style={{ height: '28px' }}
            fontSize={'12px'}
            top={'28px'}
            containerHeight={'150px'}
          />
        </div>
      )}
      {OtherInput && (
        <div className="left__panel__body__input__dropdown__container left__panel__body_input__border">
          <span className="input__label">Sequence</span>
          <input
            className="payload_form_sequence_input_modal"
            autoFocus
            placeholder="Enter Sequence"
            value={otherSequence}
            onChange={(e) => { handleOtherInputSequenceChange(e) }}
          />
          <div className="input-close-button" onClick={() => { clearPayloadForm() }} >
            <i className="fe fe-x close-icon fs-14"></i>
          </div>
        </div>
      )}
      <div className="left__panel__body__input__option__container">
        <span className="input__label">Parameter</span>
        <div className="input__box__container seventy__width" style={{ width: '14.06vw' }}>
          <ModalInput
            value={seqParams}
            handleChange={(value) => { setSeqParams(value) }}
            placeHolder="Parameter"
            maxLength={26}
          />
        </div>
      </div>
      {/* <div className="left__panel__body__input__option__container w-48">
            <span className="input__label">Min Duration</span>
            <div className="input__box__container seventy__width">
              <ModalInput
                unit="sec"
                value={seqMinDuration}
                handleChange={(value) => {
                  setSeqMinDuration(value);
                }}
            />
          </div> */}
      <div className="left__panel__body__input__option__container">
        <span className="input__label">Max Duration</span>
        <div className="input__box__container seventy__width with__unit">
          <ModalInput
            unit="sec"
            value={seqMaxDuration}
            handleChange={(value) => { setSeqMaxDuration(value) }}
            inputType="number"
            maxLength={5}
          />
        </div>
      </div>
      {/* <div className="left__panel__body__input__option__container">
            <span className="input__label">Power</span>
              <div className="input__box__container seventy__width with__unit">
                <ModalInput
                      unit="W"
                      value={seqPowerReq}
                      handleChange={(value) => {
                      setSeqPowerReq(value);
                }}
              />
              </div>
          </div> */}
    </Fragment>
  );
};

export default PayloadForm;
